import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SampleVideosGallery from "@/components/SampleVideosGallery";
import AssemblyLineWalkthrough from "@/components/AssemblyLineWalkthrough";
import {
    Lightning,
    Flask,
    Robot,
    Trophy,
    BookBookmark,
    Quotes,
    CheckCircle,
    XCircle,
    Hourglass,
    Crown,
    ArrowRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import NavBar from "@/components/NavBar";

const HERO_BG =
    "https://images.unsplash.com/photo-1759735541630-036eefb7cd3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwYWJzdHJhY3QlMjB0ZWNoJTIwYmx1ZXxlbnwwfHx8fDE3NzcxNDg3Mzd8MA&ixlib=rb-4.1.0&q=85";
const FOUNDER_PORTRAIT =
    "https://images.unsplash.com/photo-1615040416803-ee19a3b5a750?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwb3J0cmFpdCUyMG1hbGUlMjBzdHVkaW8lMjBkYXJrJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc3MTQ4NzU2fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
    const [capacity, setCapacity] = useState({
        seats_remaining: 74,
        capacity_percentage: 92.6,
        seats_taken: 926,
    });

    useEffect(() => {
        api.get("/stats/capacity").then((r) => setCapacity(r.data)).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
            <NavBar />

            {/* HERO */}
            <section className="relative isolate overflow-hidden">
                <div
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage: `url(${HERO_BG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-background z-0" />
                <div className="absolute inset-0 grid-bg opacity-40 z-0" />

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-24 pb-32">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/5 mb-8">
                            <span className="w-2 h-2 bg-electric-blue rounded-full animate-pulse" />
                            <span className="label-mono text-[11px] text-electric-blue">
                                FOUNDER COHORT — LIMITED ACCESS
                            </span>
                        </div>

                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-6">
                            STOP GUESSING.
                            <br />
                            <span className="text-electric-blue neon-text">START GROWING.</span>
                        </h1>

                        <p className="font-heading text-xl md:text-2xl text-muted-foreground italic font-light mb-10 max-w-2xl">
                            The AI-Powered Assembly Line for Faceless Creators.
                        </p>

                        <p className="text-base md:text-lg text-foreground/80 max-w-2xl mb-12 leading-relaxed">
                            Join an elite syndicate of <span className="text-electric-blue font-semibold">1,000 creators</span>{" "}
                            using proprietary "Viral Recipes" to build digital media empires —
                            without ever showing your face.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-14">
                            <Link
                                to="/signup"
                                data-testid="hero-cta-claim-seat"
                                className="group inline-flex items-center justify-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.6)]"
                            >
                                Claim Founder Seat
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" weight="bold" />
                            </Link>
                            <a
                                href="#pricing"
                                data-testid="hero-secondary-cta"
                                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-electric-blue text-white px-8 py-4 text-sm tracking-wider uppercase transition-all"
                            >
                                See Pricing Tiers
                            </a>
                        </div>

                        {/* Scarcity Bar */}
                        <ScarcityBar capacity={capacity} />
                    </motion.div>
                </div>
            </section>

            {/* THE ASSEMBLY LINE — Elevator Pitch */}
            <section id="pillars" className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-16">
                    <div className="lg:col-span-7">
                        <p className="label-mono mb-3">THE ASSEMBLY LINE</p>
                        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-tight mb-6">
                            Not a login. <br />
                            A <span className="text-electric-blue">Digital Assembly Line</span>.
                        </h2>
                        <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                            Three pillars of 2026 content creation. One workflow.
                            Zero guesswork. Every operator inside the Lab gets the same machine —
                            you just pick the niche it builds.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative aspect-square overflow-hidden border border-electric-blue/30">
                            <img
                                src="https://images.unsplash.com/photo-1588007374946-c79543903e8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwdGVjaG5vbG9neSUyMG5lb258ZW58MHx8fGJsdWV8MTc3NzMxNjQyMXww&ixlib=rb-4.1.0&q=85"
                                alt="Digital Assembly Line"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-electric-blue/10 via-transparent to-black/50" />
                        </div>
                    </div>
                </div>

                {/* 3 Pillars */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 mb-14">
                    <div data-testid="pillar-blueprint" className="bg-[#0A0A0A] p-8 hover:bg-[#0F0F10] hover:border-electric-blue group transition-all">
                        <p className="label-mono text-electric-blue mb-3">PILLAR 01</p>
                        <h3 className="font-heading text-2xl font-black tracking-tight mb-3 group-hover:text-electric-blue transition-colors">
                            The Blueprint
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                            The <span className="text-white font-semibold">Recipe Vault</span> — proven, step-by-step
                            blueprints for the highest-paying niches. From <span className="text-electric-blue">Military Tech</span> to <span className="text-electric-blue">Dark Stoicism</span> to <span className="text-electric-blue">Pet Lifestyle</span>.
                            Pick a winner. Skip the guesswork.
                        </p>
                    </div>
                    <div data-testid="pillar-tools" className="bg-[#0A0A0A] p-8 hover:bg-[#0F0F10] hover:border-electric-blue group transition-all">
                        <p className="label-mono text-electric-blue mb-3">PILLAR 02</p>
                        <h3 className="font-heading text-2xl font-black tracking-tight mb-3 group-hover:text-electric-blue transition-colors">
                            The AI Tools
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                            Our proprietary <span className="text-white font-semibold">Script Generator</span> (Gemini 3 Flash) and <span className="text-white font-semibold">Prompt Lab</span>.
                            Idea to viral-ready 60-second script + cinematic visual storyboard in
                            <span className="text-electric-blue"> under 60 seconds</span>.
                        </p>
                    </div>
                    <div data-testid="pillar-alpha" className="bg-[#0A0A0A] p-8 hover:bg-[#0F0F10] hover:border-electric-blue group transition-all">
                        <p className="label-mono text-electric-blue mb-3">PILLAR 03</p>
                        <h3 className="font-heading text-2xl font-black tracking-tight mb-3 group-hover:text-electric-blue transition-colors">
                            The Alpha
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                            Weekly <span className="text-white font-semibold">Trend Reports</span> that tell you
                            exactly what the algorithm wants <span className="text-electric-blue italic">before</span> everyone
                            else starts posting it. Five high-RPM niches delivered every Monday, 09:00 UTC.
                        </p>
                    </div>
                </div>

                {/* 3-Step Process Strip */}
                <div data-testid="process-strip" className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                        { n: "01", title: "Select a Recipe", body: "Pick a proven niche from the Vault. No brainstorming. No 'what should I post?' loops." },
                        { n: "02", title: "Generate the Soul", body: "Our AI writes the script and renders the cinematic visuals. 60-second turnaround." },
                        { n: "03", title: "Deploy", body: "Follow the Marketing Playbook to post across TikTok, Reels, and Shorts simultaneously." },
                    ].map((step) => (
                        <div key={step.n} data-testid={`process-step-${step.n}`} className="bg-[#0A0A0A] border border-electric-blue/20 p-6 relative overflow-hidden">
                            <div className="absolute -top-2 -right-2 font-heading text-7xl font-black text-electric-blue/10 leading-none">
                                {step.n}
                            </div>
                            <p className="label-mono text-electric-blue mb-2 relative">STEP {step.n}</p>
                            <h3 className="font-heading text-xl font-bold tracking-tight mb-2 relative">{step.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed relative">{step.body}</p>
                        </div>
                    ))}
                </div>

                {/* Three Income Streams */}
                <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-5">
                        <p className="label-mono mb-3">THE PAYLOAD</p>
                        <h3 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight mb-4">
                            Three income streams.
                            <br />
                            <span className="text-electric-blue">One faceless empire.</span>
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                            We focus on <span className="text-white font-semibold">Faceless Empires</span> because they're
                            scalable digital assets. You don't show your face. You don't need a camera. You build something you can
                            <span className="text-electric-blue"> sell</span>.
                        </p>
                        <div className="relative aspect-video overflow-hidden border border-white/10">
                            <img
                                src="https://images.pexels.com/photos/12920750/pexels-photo-12920750.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Digital growth visualization"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        </div>
                    </div>
                    <div className="lg:col-span-7 space-y-3">
                        {[
                            {
                                label: "STREAM 01",
                                title: "Platform Ad Revenue",
                                body: "TikTok Creativity Program + YouTube AdSense. We target high-RPM niches like Finance, Defense, and Tech.",
                            },
                            {
                                label: "STREAM 02",
                                title: "Affiliate & Lead Gen",
                                body: "Every video is a funnel. Link products and services that pay you a commission for every sale your videos generate.",
                            },
                            {
                                label: "STREAM 03",
                                title: "Asset Flipping",
                                body: "A faceless channel with 100K loyal followers is a sellable business. We teach you how to exit for a 5- or 6-figure payday.",
                            },
                        ].map((s, i) => (
                            <div key={s.label} data-testid={`income-stream-${i}`} className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue/50 p-5 transition-all flex items-start gap-4">
                                <div className="label-mono text-electric-blue text-[10px] flex-shrink-0 mt-1">{s.label}</div>
                                <div>
                                    <h4 className="font-heading font-bold text-lg mb-1">{s.title}</h4>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SAMPLE VIDEOS — niche-tagged gallery so prospects SEE the output before reading features */}
            <SampleVideosGallery />

            {/* ASSEMBLY LINE — animated 6-step walkthrough + admin-flagged "full demo" video */}
            <AssemblyLineWalkthrough />

            {/* WHY JOIN */}
            <section id="why" className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24">
                <div className="mb-12">
                    <p className="label-mono mb-3">WHAT'S INSIDE THE LAB</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-4">
                        Tools forged for the <span className="text-electric-blue">finish line</span>.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                    {WHY_BULLETS.map((b) => (
                        <motion.div
                            key={b.title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: WHY_BULLETS.indexOf(b) * 0.08 }}
                            data-testid={`why-card-${WHY_BULLETS.indexOf(b)}`}
                            className="bg-[#0A0A0A] p-8 hover:bg-[#0F0F10] hover:border-electric-blue group transition-all min-h-[260px] flex flex-col"
                        >
                            <b.icon size={32} weight="duotone" className="text-electric-blue mb-6" />
                            <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-electric-blue transition-colors">
                                {b.title}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{b.body}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CASE STUDIES */}
            <section id="case-studies" className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24">
                <div className="mb-12 text-center">
                    <p className="label-mono mb-3">MEMBER SUCCESS STORIES</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-4">
                        Real operators. <span className="text-electric-blue">Real results.</span>
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Here's what happens when you stop guessing and start following the Assembly Line.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Sarah's Story */}
                    <div data-testid="case-study-sarah" className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue transition-all overflow-hidden group">
                        <div className="aspect-[16/9] relative overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1617899644490-fbead7fb6183?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxjb250ZW50JTIwY3JlYXRvciUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3NzczMTY0MjZ8MA&ixlib=rb-4.1.0&q=85"
                                alt="Sarah's success story"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 p-6">
                                <p className="label-mono text-electric-blue mb-2">CASE STUDY #001</p>
                                <h3 className="font-heading text-2xl font-black tracking-tight">Sarah J.</h3>
                                <p className="text-sm text-muted-foreground mt-1">Vanished Empire Niche</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                                Full-time office manager, 2 hours per night. Selected Recipe #001 (Lost Tech of Ancient Egypt). 
                                Her third video hit <span className="text-electric-blue font-semibold">450K views</span>. 
                                First month: <span className="text-electric-blue font-semibold">$500 profit</span> from TikTok + affiliate links. 
                                Built a <span className="text-electric-blue font-semibold">15K-follower asset</span> worth $1,500.
                            </p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                    <Trophy size={14} weight="fill" className="text-electric-blue" />
                                    $500/month
                                </span>
                                <span className="text-white/30">•</span>
                                <span>45 min/day</span>
                            </div>
                        </div>
                    </div>

                    {/* Jerry's Story */}
                    <div data-testid="case-study-jerry" className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue transition-all overflow-hidden group">
                        <div className="aspect-[16/9] relative overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1617899615427-8ec249947f67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxjb250ZW50JTIwY3JlYXRvciUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3NzczMTY0MjZ8MA&ixlib=rb-4.1.0&q=85"
                                alt="Jerry's success story"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 p-6">
                                <p className="label-mono text-electric-blue mb-2">CASE STUDY #002</p>
                                <h3 className="font-heading text-2xl font-black tracking-tight">Jerry M.</h3>
                                <p className="text-sm text-muted-foreground mt-1">Military Tech & Engineering</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                                Retired trade professional. Used Recipe #008 (Tactical Titan) for drone engineering breakdowns. 
                                Hit <span className="text-electric-blue font-semibold">100K views</span> at a premium <span className="text-electric-blue font-semibold">$12 RPM</span>. 
                                Month 3: <span className="text-electric-blue font-semibold">$1,700/month</span> from AdSense + tactical gear sponsorship. 6 hours/week.
                            </p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                    <Trophy size={14} weight="fill" className="text-electric-blue" />
                                    $1,700/month
                                </span>
                                <span className="text-white/30">•</span>
                                <span>6 hrs/week</span>
                            </div>
                        </div>
                    </div>

                    {/* Bailey's Story */}
                    <div data-testid="case-study-bailey" className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue transition-all overflow-hidden group">
                        <div className="aspect-[16/9] relative overflow-hidden">
                            <img
                                src="https://images.pexels.com/photos/5083605/pexels-photo-5083605.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Bailey's success story"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 p-6">
                                <p className="label-mono text-electric-blue mb-2">CASE STUDY #003</p>
                                <h3 className="font-heading text-2xl font-black tracking-tight">Bailey R.</h3>
                                <p className="text-sm text-muted-foreground mt-1">Pet Lifestyle & Heartwarming</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                                College student. Used Recipe #005 (Canine Connection) for emotional pet content. 
                                One video went <span className="text-electric-blue font-semibold">mega-viral: 2.4M views</span> in 72 hours. 
                                Month 2: <span className="text-electric-blue font-semibold">$2,150 profit</span> from TikTok Creativity Program + Amazon commissions.
                            </p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                    <Trophy size={14} weight="fill" className="text-electric-blue" />
                                    $2,150/month
                                </span>
                                <span className="text-white/30">•</span>
                                <span>2.4M views</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FAQ / Operator's Guide CTA — full breakdown of what you get */}
                <div className="mt-14 flex flex-col items-center text-center">
                    <p className="label-mono text-electric-blue mb-3">WANT THE FULL BREAKDOWN?</p>
                    <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-3 max-w-2xl">
                        See exactly what you get, what each Operator did, and{" "}
                        <span className="text-electric-blue">why now is the window.</span>
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground max-w-xl mb-6">
                        Every tool. Every Recipe. Every detail of Sarah, Jerry &amp; Bailey's playbooks.
                        Plus answers to every question we get asked before someone claims a seat.
                    </p>
                    <Link
                        to="/faq"
                        data-testid="case-studies-faq-cta"
                        className="inline-flex items-center gap-2 border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-8 py-4 text-sm tracking-wider uppercase font-bold transition-all hover:shadow-[0_0_24px_rgba(0,123,255,0.5)]"
                    >
                        READ THE OPERATOR'S GUIDE &amp; FAQ
                        <ArrowRight size={16} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24">
                <div className="mb-12 text-center">
                    <p className="label-mono mb-3">THE FOUNDER'S OFFER</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-4">
                        Lock your <span className="text-electric-blue">$20/mo</span> rate. For life.
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Once Seat #1,000 is claimed, the Lab closes to new members until 2027.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <PricingTier
                        tier="Pioneer"
                        seats="Seats 1–100"
                        price="$15"
                        oldPrice
                        status="SOLD OUT"
                        statusColor="bg-red-500/10 text-red-400 border-red-500/30"
                        statusIcon={XCircle}
                        disabled
                        testid="pricing-pioneer"
                    />
                    <PricingTier
                        tier="Founder"
                        seats="Seats 101–500"
                        price="$20"
                        priceSuffix="/mo"
                        status="ACTIVE"
                        statusColor="bg-electric-blue/15 text-electric-blue border-electric-blue/50"
                        statusIcon={CheckCircle}
                        highlighted
                        testid="pricing-founder"
                    />
                    <PricingTier
                        tier="Standard"
                        seats="Seats 501–1,000"
                        price="$29"
                        priceSuffix="/mo"
                        status="WAITLIST"
                        statusColor="bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                        statusIcon={Hourglass}
                        disabled
                        testid="pricing-standard"
                    />
                    <PricingTier
                        tier="Lifetime ★"
                        seats="One Payment"
                        price="$497"
                        priceSuffix="once"
                        status="LIFETIME"
                        statusColor="bg-yellow-400/10 text-yellow-300 border-yellow-400/40"
                        statusIcon={Crown}
                        gold
                        testid="pricing-lifetime"
                    />
                </div>

                <div className="mt-10 flex justify-center">
                    <Link
                        to="/signup"
                        data-testid="pricing-cta-claim"
                        className="group inline-flex items-center justify-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-10 py-5 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)] animate-glow-pulse"
                    >
                        YES! Claim my Founder Seat NOW
                        <ArrowRight size={18} weight="bold" />
                    </Link>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-3 italic">
                    Lock in your $20/mo rate for the life of your membership.
                </p>
            </section>

            {/* FOUNDER QUOTE */}
            <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5 relative">
                        <div className="aspect-[4/5] relative overflow-hidden border border-white/10">
                            <img
                                src={FOUNDER_PORTRAIT}
                                alt="Steve E., Founder"
                                className="w-full h-full object-cover grayscale-[20%]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            <div className="absolute top-4 left-4 px-3 py-1 bg-electric-blue/15 border border-electric-blue/40">
                                <span className="label-mono text-[10px] text-electric-blue">THE ARCHITECT</span>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <Quotes size={40} weight="fill" className="text-electric-blue mb-6" />
                        <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl font-light leading-tight tracking-tight mb-8">
                            "I built the Nexus Creator Lab for the person who is tired of the
                            <span className="text-electric-blue"> 'AI noise' </span>
                            and just wants a system that works. We don't just give you tools;
                            we give you the <span className="text-electric-blue">finish line</span>."
                        </blockquote>
                        <div className="flex items-center gap-4">
                            <div className="w-px h-12 bg-electric-blue" />
                            <div>
                                <p className="font-heading font-bold text-lg">Steve E.</p>
                                <p className="label-mono text-electric-blue mt-1">FOUNDER, NEXUS CREATOR LAB</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="relative border-t border-white/10 mt-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Lightning weight="fill" size={16} className="text-electric-blue" />
                        <span className="label-mono text-xs">NEXUS.LAB © 2026</span>
                    </div>
                    <div className="flex items-center gap-5">
                        <Link to="/terms" data-testid="footer-terms-link" className="label-mono text-xs text-muted-foreground hover:text-electric-blue transition-colors">
                            TERMS
                        </Link>
                        <Link to="/privacy" data-testid="footer-privacy-link" className="label-mono text-xs text-muted-foreground hover:text-electric-blue transition-colors">
                            PRIVACY
                        </Link>
                        <span className="label-mono text-xs text-muted-foreground">
                            VEGAS-TECH BUILD
                        </span>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function ScarcityBar({ capacity }) {
    const pct = Math.min(99.9, capacity.capacity_percentage || 92);
    return (
        <div data-testid="scarcity-bar" className="max-w-2xl glass-panel p-6 relative overflow-hidden">
            <div className="absolute inset-0 grain-overlay pointer-events-none" />
            <div className="flex justify-between items-center mb-3">
                <span className="label-mono text-electric-blue">CAPACITY</span>
                <span className="font-mono text-electric-blue font-bold text-lg">{pct}%</span>
            </div>
            <div className="relative h-2 bg-[#111] overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.4, ease: "easeOut" }}
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-electric-blue to-cyan-400 shadow-[0_0_18px_rgba(0,123,255,0.7)]"
                />
            </div>
            <p className="mt-4 text-sm text-foreground/80">
                Only{" "}
                <span data-testid="scarcity-seats-remaining" className="text-electric-blue font-bold text-lg">
                    {capacity.seats_remaining} seats
                </span>{" "}
                remain in the Founding Member cohort. Once Seat #1,000 is claimed, the Lab closes to
                new members until <span className="text-electric-blue">2027</span>.
            </p>
        </div>
    );
}

function PricingTier({ tier, seats, price, oldPrice, priceSuffix = "/ month", status, statusColor, statusIcon: Icon, highlighted, disabled, gold, testid }) {
    return (
        <div
            data-testid={testid}
            className={`relative p-8 transition-all ${
                highlighted
                    ? "bg-[#0A0A0A] border border-electric-blue neon-border"
                    : gold
                    ? "bg-gradient-to-br from-[#0A0A0A] to-[#1a1408] border border-yellow-400/50 hover:-translate-y-1 shadow-[0_0_24px_rgba(250,204,21,0.18)]"
                    : "bg-[#0A0A0A] border border-white/10"
            } ${disabled ? "opacity-50" : ""} ${!highlighted && !gold && !disabled ? "hover:-translate-y-1" : ""}`}
        >
            {highlighted && (
                <div className="absolute -top-3 left-8 px-3 py-1 bg-electric-blue text-white">
                    <span className="label-mono text-[10px]">RECOMMENDED</span>
                </div>
            )}
            {gold && (
                <div className="absolute -top-3 left-8 px-3 py-1 bg-yellow-400 text-black">
                    <span className="label-mono text-[10px] font-bold">★ BEST VALUE</span>
                </div>
            )}
            <p className="label-mono mb-2">{seats}</p>
            <h3 className={`font-heading text-2xl font-black tracking-tight mb-6 ${gold ? "text-yellow-300" : ""}`}>{tier}</h3>
            <div className="flex items-baseline gap-2 mb-6">
                <span
                    className={`font-heading text-5xl font-black ${
                        oldPrice
                            ? "line-through text-muted-foreground"
                            : highlighted
                            ? "text-electric-blue"
                            : gold
                            ? "text-yellow-300"
                            : "text-white"
                    }`}
                >
                    {price}
                </span>
                <span className="text-sm text-muted-foreground">{priceSuffix}</span>
            </div>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 border ${statusColor}`}>
                <Icon size={14} weight="bold" />
                <span className="label-mono text-[11px]">{status}</span>
            </div>
            {gold && (
                <p className="text-xs text-yellow-300/70 mt-4 italic leading-relaxed">
                    Skip the subscription. Pay once. Own the entire vault forever.
                </p>
            )}
        </div>
    );
}

const WHY_BULLETS = [
    {
        icon: Lightning,
        title: "The Weekly Alpha",
        body: "Get 5 trending, high-RPM niches delivered every Monday. Stay ahead of the algorithm shift.",
    },
    {
        icon: Flask,
        title: "The Recipe Vault",
        body: "Instant access to Recipe #001 (Vanished Empires) and all future blueprints. Battle-tested formulas.",
    },
    {
        icon: Robot,
        title: "Nexus Script Engine",
        body: "Use our integrated Gemini 3 Flash tool to turn any idea into a viral 60-second script in seconds.",
    },
    {
        icon: Trophy,
        title: "Creator Standings",
        body: "Compete on the leaderboard for bragging rights and exclusive channel audits at Bronze, Silver, Gold tiers.",
    },
];
